import { useForm } from 'react-hook-form';

import { Trans, t } from '@grafana/i18n';
import { isFetchError, locationService } from '@grafana/runtime';
import { Alert, Box, Button, Field, Input, Label, Stack, TagsInput, Text, TextArea } from '@grafana/ui';
import { useAppNotification } from 'app/core/copy/appNotification';
import { getMessageFromError } from 'app/core/utils/errors';
import { dashboardAPIVersionResolver } from 'app/features/dashboard/api/DashboardAPIVersionResolver';
import { CustomDashboardTemplateInteractions } from 'app/features/dashboard-scene/analytics/dashboard-templates/main';
import { type DashboardScene } from 'app/features/dashboard-scene/scene/DashboardScene';

import { useCreateDashboardTemplateMutation } from '../api/clients/dashboardtemplates/v1alpha1';

import { buildDashboardTemplateBody } from './wire';

interface SaveAsTemplateFormDTO {
  title: string;
  description: string;
  tags: string[];
}

export interface Props {
  dashboard: DashboardScene;
}

const SOURCE_DASHBOARD_UID_ANNOTATION = 'grafana.app/sourceDashboardUid';

export function SaveAsTemplateForm({ dashboard }: Props) {
  const notifyApp = useAppNotification();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
    watch,
  } = useForm<SaveAsTemplateFormDTO>({
    mode: 'onBlur',
  });

  const [createDashboardTemplate, { isLoading: isSaving, isError, error }] = useCreateDashboardTemplateMutation();

  const onSave = async (data: SaveAsTemplateFormDTO) => {
    const dashboardSpec = dashboard.serializer.getSaveModel(dashboard);
    const dashboardVersion = `dashboard.grafana.app/${dashboardAPIVersionResolver.getV2()}`;

    const annotations: Record<string, string> = {};
    if (dashboard.state.uid) {
      annotations[SOURCE_DASHBOARD_UID_ANNOTATION] = dashboard.state.uid;
    }

    try {
      const response = await createDashboardTemplate({
        dashboardTemplate: buildDashboardTemplateBody({
          generateName: 'dashboardtemplates-',
          annotations,
          spec: {
            title: data.title,
            description: data.description,
            tags: data.tags ?? [],
            dashboardVersion,
            dashboard: dashboardSpec,
          },
        }),
      }).unwrap();

      CustomDashboardTemplateInteractions.created({
        status: 'completed',
        dashboardUid: dashboard.state.uid ?? '',
        templateUid: response.metadata?.name ?? '',
        tagCount: data.tags?.length ?? 0,
        hasDescription: !!data.description?.trim(),
      });

      notifyApp.success(t('dashboard-scene.save-as-template-form.success-toast', 'Dashboard template created'));
      locationService.partial({ templateSaved: response.metadata?.name });
      dashboard.closeModal();
    } catch (err) {
      CustomDashboardTemplateInteractions.created({
        status: 'failed',
        dashboardUid: dashboard.state.uid ?? '',
        errorStatus: isFetchError(err) ? err.status : undefined,
      });
      throw err;
    }
  };

  return (
    <form onSubmit={handleSubmit(onSave)}>
      <Stack direction="column" gap={2}>
        <Text color="secondary">
          <Trans i18nKey="dashboard-scene.save-as-template-form.description">
            Custom templates are saved as a separate resource, similar to dashboards with custom metadata. They can be
            used through the template gallery
          </Trans>
        </Text>

        <Field
          noMargin
          label={
            <Label htmlFor="title">
              <Trans i18nKey="dashboard-scene.save-as-template-form.template-title">Template title</Trans>
            </Label>
          }
          invalid={!!errors.title}
          error={errors.title?.message}
        >
          <Input
            {...register('title', {
              required: t('dashboard-scene.save-as-template-form.title-required', 'Required'),
            })}
            disabled={isSaving}
            aria-label={t('dashboard-scene.save-as-template-form.aria-label-template-title', 'Template title field')}
            autoFocus
          />
        </Field>

        <Field
          noMargin
          label={
            <Label htmlFor="description">
              <Trans i18nKey="dashboard-scene.save-as-template-form.template-description">Template description</Trans>
            </Label>
          }
          invalid={!!errors.description}
          error={errors.description?.message}
        >
          <TextArea
            {...register('description')}
            disabled={isSaving}
            aria-label={t(
              'dashboard-scene.save-as-template-form.aria-label-template-description',
              'Template description field'
            )}
          />
        </Field>

        <Field
          noMargin
          label={
            <Label htmlFor="tags">
              <Trans i18nKey="dashboard-scene.save-as-template-form.template-tags">Tags</Trans>
            </Label>
          }
        >
          <TagsInput
            tags={watch('tags')}
            onChange={(tags) => setValue('tags', tags, { shouldDirty: true })}
            disabled={isSaving}
          />
        </Field>

        <Box paddingTop={2}>
          {isError && (
            <Alert
              title={t('dashboard-scene.save-as-template-form.error-title', 'Failed to save template')}
              severity="error"
            >
              {getMessageFromError(error)}
            </Alert>
          )}
          <Stack alignItems="center">
            <Button variant="secondary" onClick={() => dashboard.closeModal()} fill="outline" disabled={isSaving}>
              <Trans i18nKey="dashboard-scene.save-as-template-form.cancel">Cancel</Trans>
            </Button>
            <Button type="submit" disabled={!isValid || isSaving} icon={isSaving ? 'spinner' : undefined}>
              {isSaving
                ? t('dashboard-scene.save-as-template-form.saving', 'Saving...')
                : t('dashboard-scene.save-as-template-form.save', 'Save')}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </form>
  );
}
